import { serializeSearchParam } from "@/lib/market/browseState";
import { myStuffPath } from "@/lib/my-stuff";

/** Visible account name before an ellipsis is added. */
export const HEADER_ACCOUNT_LABEL_MAX = 11;

export const MARKETPLACE_NAV = {
  label: "Marketplace",
  href: "/market",
} as const;

export const SELL_NAV = {
  label: "Sell an item",
  href: "/sell",
} as const;

export const LEARN_NAV = [
  {
    label: "How it works",
    mobileLabel: "How It Works",
    sectionId: "how-it-works",
    href: "/#how-it-works",
  },
  {
    label: "Testimonials",
    mobileLabel: "Testimonials",
    sectionId: "testimonials",
    href: "/#testimonials",
  },
  {
    label: "FAQs",
    mobileLabel: "FAQs",
    sectionId: "faqs",
    href: "/#faqs",
  },
] as const;

export const REQUEST_ITEM_NAV = {
  label: "Request an item",
  href: "/request-item",
} as const;

/** Same destination. Desktop uses plain language; mobile keeps its current label. */
export const REFERRAL_NAV = {
  desktopLabel: "Invite friends",
  mobileLabel: "Referral Hub",
  href: "/refer",
} as const;

export const SIGN_OUT_LABEL = "Sign out";

export type AccountMenuItemId =
  | "admin"
  | "profile"
  | "my-stuff"
  | "request-item"
  | "invite-friends";

export type AccountMenuItem = {
  id: AccountMenuItemId;
  label: string;
  href: string;
};

export function sellNavHref(isAuthenticated: boolean): string {
  if (isAuthenticated) return SELL_NAV.href;
  return `/login?redirectTo=${SELL_NAV.href}`;
}

export function isMarketplaceActive(pathname: string): boolean {
  return (
    pathname === MARKETPLACE_NAV.href ||
    pathname.startsWith(`${MARKETPLACE_NAV.href}/`)
  );
}

export function isSellActive(pathname: string): boolean {
  return pathname === SELL_NAV.href || pathname.startsWith(`${SELL_NAV.href}/`);
}

export function isLearnSectionPage(pathname: string): boolean {
  return (pathname || "/") === "/";
}

export function formatHeaderAccountLabel(
  name: string | null | undefined,
): string {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) return "Account";
  if (trimmed.length <= HEADER_ACCOUNT_LABEL_MAX) return trimmed;
  return `${trimmed.slice(0, HEADER_ACCOUNT_LABEL_MAX)}…`;
}

export function buildAccountMenuItems(options: {
  isAdmin: boolean;
  showInviteFriends: boolean;
}): AccountMenuItem[] {
  const items: AccountMenuItem[] = [];
  if (options.isAdmin) {
    items.push({ id: "admin", label: "Admin Dashboard", href: "/admin" });
  }
  items.push(
    { id: "profile", label: "Profile", href: "/profile" },
    { id: "my-stuff", label: "My stuff", href: myStuffPath() },
    {
      id: "request-item",
      label: REQUEST_ITEM_NAV.label,
      href: REQUEST_ITEM_NAV.href,
    },
  );
  if (options.showInviteFriends) {
    items.push({
      id: "invite-friends",
      label: REFERRAL_NAV.desktopLabel,
      href: REFERRAL_NAV.href,
    });
  }
  return items;
}

/**
 * Header search target. Empty queries do nothing.
 * On the market, the search param is merged so other filters stay.
 * Elsewhere, navigation starts a fresh market search.
 */
export function marketSearchHref(
  pathname: string,
  currentSearch: string,
  query: string,
): string | null {
  const trimmed = query.trim();
  if (!trimmed) return null;
  const base = isMarketplaceActive(pathname) ? currentSearch : "";
  const next = serializeSearchParam(base, trimmed);
  return next ? `${MARKETPLACE_NAV.href}?${next}` : MARKETPLACE_NAV.href;
}
